import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {IconButton} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import PriorityHighIcon from '@mui/icons-material/PriorityHighRounded';
import {DefineSupplierPopup} from './dialogs/DefineSupplierPopup.jsx';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {ezprOrange} from '../_helpers';
import {Config, BrickContext} from 'olympe';
import {useProperty} from '@olympeio/core';
import {requestStores, useApplicationStore} from '../ListOfPREditors.brick.jsx';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {tagToDisplayER} from '../../utils/tagTransforms';
import {
    getStringValueForSourceFromApiKey, LAMBDA_NAMES, SOURCES,
} from '../../utils/constants';
import {
    getAuthorizationFromDataLambda,
    deletePRLambda,
    getERSAndGRWithPromiseLambda,
} from '../../utils/functionSignatures';
import {getVendorCodes, removeLeadingZeros, replaceJSX} from '../../utils/helpers';
import {fetchAndProcessIncoterms} from '../../utils/incotermsHelpers';
import {OneTimeVendor} from '../../models/OneTimeVendor.js';
import UserController from '../../controllers/UserController.js';
import {ThreeDotsMenuShrunkenPR} from './menus/ThreeDotsMenuShrunkenPR.jsx';
import {PurchaseRequestController} from '../../controllers/PurchaseRequestController';
import {COUNTRIES} from './utils/CountryData';
import {COLUMN_CONTENT_CONFIG} from '../../utils/columnsConfig.jsx';
import {StatusBadge} from '../StatusBadge.jsx';
import TwoToneCheckbox from '../TwoToneCheckbox.jsx';

const PRIORITY_HIGH_ICON_SX = {color: '#d42e31', fontSize: '18px'};
const READABLE_DATE_OPTIONS = {month: 'short', day: 'numeric', year: 'numeric'};

/**
 * @param {ShrunkenPREditor.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function ShrunkenPREditor(props) {
    const [open, setOpen] = useState(false);
    const [copyToClipboardText, setCopyToClipboardText] = useState(`Copy PR's ID`);
    const [copyToClipboardTextPrNumber, setCopyToClipboardTextPrNumber] = useState(`Copy PR's number`);
    const [copyToClipboardLeaveTime, setCopyToClipboardLeaveTime] = useState(0);
    const [showUnauthorizedDefineSupplierTooltip, setShowUnauthorizedDefineSupplierTooltip] = useState(false);
    const [defineSupplierTooltipText, setDefineSupplierTooltipText] = useState('');
    const [defineSupplierLoading, setDefineSupplierLoading] = useState(false);
    const [expandLoading, setExpandLoading] = useState(false);

    const useRequestStore = requestStores.get(props.prTag);

    const currentTab = useProperty(props.context, 'Current tab', false);
    const [vendorName, setVendorName] = useRequestStore((state) => [state.vendorName, state.setVendorName]);
    const [vendorCode, setVendorCode] = useRequestStore((state) => [state.vendorCode, state.setVendorCode]);
    const setOTVName = useRequestStore((state) => state.setOTVName);
    const setOTVAddress = useRequestStore((state) => state.setOTVAddress);
    const setOTVPhoneNumber = useRequestStore((state) => state.setOTVPhoneNumber);
    const editableSupplier = useRequestStore((state) => state.editableSupplier);
    const creationDate = useRequestStore((state) => state.creationDate);
    const materials = useRequestStore((state) => state.materials);
    const spender = useRequestStore((state) => state.spender);
    const requester = useRequestStore((state) => state.requester);
    const prNumber = useRequestStore((state) => state.prNumber);
    const source = useRequestStore((state) => state.source);
    const isCandex = source === SOURCES.CANDEX;
    const isPunchOut = source === SOURCES.PUNCHOUT;
    const isNestBuy = source === SOURCES.NESTBUY;
    const sourceFromApiKey = useRequestStore((state) => state.sourceFromApiKey);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const purchasingGroup = useRequestStore((state) => state.purchasingGroup);
    const defaultPlant = useRequestStore((state) => state.defaultPlant);
    const companyCode = useRequestStore((state) => state.companyCode);
    const setErs = useRequestStore((state) => state.setErs);
    const setGrBasedInvoiceVerification = useRequestStore((state) => state.setGrBasedInvoiceVerification);
    const setSupplierType = useRequestStore((state) => state.setSupplierType);
    const lastActiveUser = useRequestStore((state) => state.lastActiveUser);
    const getPR = useRequestStore((state) => state.getPurchaseRequest);
    const projectTitle = useRequestStore((state) => state.projectTitle);
    const strategicBuyers = useRequestStore((state) => state.strategicBuyers);
    const EPRId = useRequestStore((state) => state.EPRId);
    const status = useRequestStore((state) => state.status);
    const [userHasAccessToProperties, setUserHasAccessToProperties] = useRequestStore((state) => [state.userHasAccessToProperties, state.setUserHasAccessToProperties]);
    const submissionDate = useRequestStore((state) => state.submissionDate);
    const systemAlias = useRequestStore((state) => state.systemAlias);
    const columnsToDisplay = useApplicationStore((state) => state.columnsToDisplay);
    const setIncoterms = useRequestStore((state) => state.setIncoterms);
    const sourceToDisplay = useRequestStore((state) => state.sourceToDisplay);

    const getERSAndGR = props.context.get(LAMBDA_NAMES.GET_ERS_AND_GR);
    const authLambda = props.context.get(LAMBDA_NAMES.GET_AUTHORIZATION_FROM_DATA);
    const deletePR = props.context.get(LAMBDA_NAMES.DELETE_PR);
    const getSupplierPurchaseOrgLink = props.context.get(LAMBDA_NAMES.GET_SUPPLIER_PURCHASE_ORG_LINK);
    const getAllIncoterms = props.context.get(LAMBDA_NAMES.GET_ALL_INCOTERMS);

    const showButtons = PurchaseRequestController.getAvailableActions(spender, requester, status);

    const [failedPR, setFailedPR] = useState(null);
    const [isSelected, setIsSelected] = useState(false);

    const errorTitle = failedPR ? failedPR.errorTitle : '';
    const errorMessages = failedPR ? failedPR.errorMessages : [];

    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        const handleCustomEvent = (event) => {
            if (!isMounted.current) return;
            setOpen(event.detail.prTag === props.prTag);
            props.handleClickOnSupplier(event.detail.prTag === props.prTag);
        };

        window?.addEventListener('openVendorDialog', handleCustomEvent);

        let scrollTimer = null;
        if (props.prTag === props.prIdFromQueryParam) {
            // Expand the PR coming from query string.
            handleExpandPR();
            // setExpanded(true);
            // Scroll to the specific PR when page loads.
            scrollTimer = setTimeout(() => {
                if (isMounted.current) {
                    document
                        ?.getElementById(`pr-${props.prTag}`)
                        ?.scrollIntoView({behavior: 'smooth'});
                }
            }, 700);
            props.removePrIdFromRef();
        }

        return () => {
            window?.removeEventListener('openVendorDialog', handleCustomEvent);
            clearTimeout(scrollTimer);
        };
    }, []);

    // This useEffect is used to update all variables required for mass submit
    useEffect(() => {
        if (!isMounted.current) return;
        const isPRFailed = structuredClone(props.failedMassSubmittedPRs.find((item) => item.prTag === props.prTag));
        if (isPRFailed) {
            isPRFailed.errorMessages = replaceJSX(isPRFailed.errorMessages, '<POP-UP>', <a href="#" onClick={(e)=> {
                e.preventDefault();
                setOpen(true);
                props.handleClickOnSupplier(true);
            }}>pop-up</a> );
        }
        setFailedPR(isPRFailed || null);

        setIsSelected(props.selectedPRs.includes(props.prTag));
    }, [props.failedMassSubmittedPRs, status, props.selectedPRs, props.prTag]);

    const handleClickOpen = useCallback((event) => {
        event.stopPropagation();
        if (spender.value === UserController.getCurrentUserName('EZPR-USER') ||
            requester.value === UserController.getCurrentUserName('EZPR-USER') ||
            currentTab === PurchaseRequest.tab.pendingSb) {
            props.handleClickOnSupplier(true);
            setOpen(true);
            setUserHasAccessToProperties(true);
        } else {
            setDefineSupplierLoading(true);
            getAuthorizationFromDataLambda(props.context,
                authLambda,
                UserController.getCurrentUserName('CAADAPAAM'),
                purchaseOrganization.value,
                purchasingGroup.value,
                defaultPlant.value,
                companyCode.value,
                (result) => {
                    if (PurchaseRequestController.userHasAccessToPrData(JSON.parse(result), getPR().getPurchaseRequest())) {
                        setUserHasAccessToProperties(JSON.parse(result), getPR().getPurchaseRequest());
                        setOpen(true);
                        props.handleClickOnSupplier(true);
                        setDefineSupplierLoading(false);
                    } else {
                        setDefineSupplierTooltipText(`You don't have the authorization to modify this PR`);
                        setShowUnauthorizedDefineSupplierTooltip(true);
                        setDefineSupplierLoading(false);
                    }
                },
                () => {
                    setDefineSupplierTooltipText(`There's an issue with the authorization system. Please try again later.`);
                    setShowUnauthorizedDefineSupplierTooltip(true);
                    setDefineSupplierLoading(false);
                });
        }
    }, [authLambda,
        purchaseOrganization.value,
        purchasingGroup.value,
        defaultPlant.value,
        companyCode.value,
        currentTab,
        spender.value,
        requester.value,
        props.handleClickOnSupplier,
        props.context,
        getPR,
        setUserHasAccessToProperties]);

    const handleExpandPR = useCallback(() => {
        if (userHasAccessToProperties === null &&
            currentTab !== PurchaseRequest.tab.submitted) {
            // In Open Requests tab, if current user is spender or requester, no need to check the auth data
            const giveAccessInOpenRequests = currentTab === PurchaseRequest.tab.open && (
                spender.value.toUpperCase() === UserController.getCurrentUserName('EZPR-USER').toUpperCase() ||
                requester.value.toUpperCase() === UserController.getCurrentUserName('EZPR-USER').toUpperCase());

            // In Pending SB approval tab, if current user is part of the SB list, no need to check the auth data
            const giveAccessInSBTab = status === PurchaseRequest.statuses.pendingSB && (
                strategicBuyers.some((sb) => sb.value.toUpperCase() === UserController.getCurrentUserName('EZPR-USER').toUpperCase())
            );

            const giveAccess = giveAccessInOpenRequests || giveAccessInSBTab;

            if (giveAccess) {
                setUserHasAccessToProperties(true);
                props.handleClickOnExpand();
            } else {
                setExpandLoading(true);
                getAuthorizationFromDataLambda(props.context,
                    authLambda,
                    UserController.getCurrentUserName('CAADAPAAM'),
                    purchaseOrganization.value,
                    purchasingGroup.value,
                    defaultPlant.value,
                    companyCode.value,
                    (result) => {
                        setExpandLoading(false);
                        setUserHasAccessToProperties(JSON.parse(result), getPR().getPurchaseRequest());
                        props.handleClickOnExpand();
                    },
                    () => {
                        setExpandLoading(false);
                        props.handleClickOnExpand();
                    });
            }
        } else {
            props.handleClickOnExpand();
        }
    }, [userHasAccessToProperties,
        currentTab,
        status,
        strategicBuyers,
        authLambda,
        purchaseOrganization.value,
        purchasingGroup.value,
        defaultPlant.value,
        companyCode.value,
        props.context,
        getPR,
        props.handleClickOnExpand,
        spender.value,
        requester.value]);

    const handleCancelDefineSupplierPopup = () => {
        setOpen(false);
        props.handleClickOnSupplier(false);
    };

    const countrySAPNamefromName = (name) => {
        return COUNTRIES.find((c) => c.name === name)?.sapName || '';
    };

    const handleClosePunchOut = async (genericVendor) => {
        await handleCloseDefineSupplierPopup(false, genericVendor, undefined, false, false, []);
    };

    const handleCloseDefineSupplierPopup = async (isOneTimeVendorSelected, genericVendor, OTVInfo, isCollectiveMedicalVendorSelected, isMultiTimeVendor, attachments) => {
        setOpen(false);
        props.handleClickOnSupplier(false);
        let newVendorCode;
        const [OTVCode, CMVCode, MTVCode] = getVendorCodes(Config.getParameter('ENV'));

        if (isOneTimeVendorSelected) {
            setVendorName(OTVInfo.name);
            setOTVAddress(
                {
                    name: '',
                    name2: OTVInfo.name2,
                    street: OTVInfo.street,
                    state: OTVInfo.state,
                    city: OTVInfo.city,
                    houseNumber: OTVInfo.houseNumber,
                    postalCode: OTVInfo.postalCode,
                    country: countrySAPNamefromName(OTVInfo.country),
                });
            setOTVName(OTVInfo.name);
            setOTVPhoneNumber(OTVInfo.phoneNumber);
            setVendorCode(OTVCode);
            newVendorCode = OTVCode;
            setSupplierType(OneTimeVendor.supplierTypes.otv);
        } else if (genericVendor) {
            const idxOfHyphen = genericVendor.indexOf(' - ');
            setVendorCode(genericVendor.substring(0, idxOfHyphen));
            setVendorName(genericVendor.substring(idxOfHyphen + 3));
            newVendorCode = genericVendor.substring(0, idxOfHyphen);

            setOTVName('');
            setOTVPhoneNumber('');
            setOTVAddress({
                name: '',
                name2: '',
                houseNumber: '',
                street: '',
                state: '',
                city: '',
                postalCode: '',
                country: '',
            });
            setSupplierType(OneTimeVendor.supplierTypes.generic);
        } else if (isCollectiveMedicalVendorSelected) {
            setVendorName(OTVInfo.name);
            setOTVAddress(
                {
                    name: '',
                    name2: OTVInfo.name2,
                    street: OTVInfo.street,
                    state: OTVInfo.state,
                    city: OTVInfo.city,
                    houseNumber: OTVInfo.houseNumber,
                    postalCode: OTVInfo.postalCode,
                    country: countrySAPNamefromName(OTVInfo.country),
                });
            setOTVName(OTVInfo.name);
            setOTVPhoneNumber(OTVInfo.phoneNumber);
            setVendorCode(CMVCode);
            newVendorCode = CMVCode;
            setSupplierType(OneTimeVendor.supplierTypes.cmv);
        } else if (isMultiTimeVendor) {
            setVendorName(OTVInfo.name);
            setOTVAddress({
                name: '',
                name2: OTVInfo.name2,
                street: OTVInfo.street,
                state: OTVInfo.state,
                city: OTVInfo.city,
                houseNumber: OTVInfo.houseNumber,
                postalCode: OTVInfo.postalCode,
                country: countrySAPNamefromName(OTVInfo.country),
            });
            setOTVName(OTVInfo.name);
            setOTVPhoneNumber(OTVInfo.phoneNumber);
            setVendorCode(MTVCode);
            newVendorCode = MTVCode;
            setSupplierType(OneTimeVendor.supplierTypes.mtv);
        }

        const incotermsPromise = fetchAndProcessIncoterms({
            context: props.context,
            getIncoterms: getSupplierPurchaseOrgLink,
            getAllIncoterms: getAllIncoterms,
            vendorCode: newVendorCode,
            purchaseOrgValue: purchaseOrganization.value,
        });

        const getERSAndGRPromise = getERSAndGRWithPromiseLambda(
            props.context,
            getERSAndGR,
            newVendorCode,
            purchaseOrganization.value,
        );

        const [incotermsResult, ersAndGrResult] = await Promise.allSettled([
            incotermsPromise,
            getERSAndGRPromise,
        ]);

        if (incotermsResult.status === 'fulfilled') {
            const {code, description} = incotermsResult.value;
            setIncoterms({
                data: {
                    label: description,
                    value: code,
                },
                manuallyModified: false,
            });
        } else {
            console.error('Error fetching incoterms:', incotermsResult.reason);
            setIncoterms({
                data: {
                    label: '',
                    value: '',
                },
                manuallyModified: false,
            });
        }

        if (ersAndGrResult.status === 'fulfilled') {
            try {
                const ERSAndGRData = JSON.parse(
                    Array.isArray(ersAndGrResult.value) ? ersAndGrResult.value[0] : ersAndGrResult.value,
                );

                setErs(ERSAndGRData[0]?.xersy === 'X');
                setGrBasedInvoiceVerification(ERSAndGRData[0]?.webre === 'X');
            } catch (error) {
                console.error('Error processing ERS and GR data:', error);
            }
        } else {
            console.error('Error fetching ERS and GR:', ersAndGrResult.reason);
        }

        props.onSave(undefined);
    };

    const handleClickOnDeletePR = () => {
        deletePRLambda(props.context, deletePR, props.prTag).then(() => {});
    };

    const addSupplierButton = () => {
        if (editableSupplier) {
            return (
                <Tooltip
                    open={showUnauthorizedDefineSupplierTooltip}
                    onClose={() => setShowUnauthorizedDefineSupplierTooltip(false)}
                    arrow
                    enterNextDelay={500}
                    leaveDelay={3000}
                    title={defineSupplierTooltipText}>
                    {defineSupplierLoading ?
                        (<CircularProgress size={16}/>) :
                        (<IconButton
                            onClick={handleClickOpen}
                            sx={{
                                width: '24px',
                                height: '24px',
                                color: '#a1a0a0',
                                ml: '5px',
                            }}
                        >
                            <SearchIcon />
                        </IconButton>)
                    }
                </Tooltip>
            );
        } else {
            return null;
        }
    };

    const addStatusIcon = () => (
        <StatusBadge status={status.toLowerCase()} />
    );

    const addAvatar = (user) => {
        if (!user || !user.label) {
            return null;
        }
        return (<Tooltip title={user.label} placement={'top'} arrow>
            <Avatar sx={{fontSize: '18px', m: 'auto'}}>
                {user.label.split(' ').slice(0, 2).map((x) => x[0]?.toUpperCase()).join('')}
            </Avatar>
        </Tooltip>);
    };

    const addTextWithTooltip = (text, shortText) => {
        return (<Tooltip title={text} placement={'top'} arrow>
            <Box component={'p'} sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                {shortText ? shortText : text}
            </Box>
        </Tooltip>);
    };

    const addTextWithTooltipMultiLines = (text, shortText) => {
        return (<Tooltip title={text} placement={'top'} arrow>
            <div className={'pr-editor-shrunken-text'}>
                {shortText ? shortText : text}
            </div>
        </Tooltip>);
    };

    const addThreeDotsMenu = () => {
        let items = [];
        switch (currentTab) {
        case PurchaseRequest.tab.pendingSb:
            if (props.isAnyPRSelected) {
                items.push({
                    title: 'Copy',
                    onClick: props.handleMassCopy,
                    disabled: props.massCopyLoading || isCandex || isPunchOut || isNestBuy,
                });
                items.push({
                    title: 'Delete',
                    onClick: props.handleMassDelete,
                    disabled: props.massDeleteLoading,
                });
                break;
            } else {
                items = [
                    {
                        title: 'Deny',
                        onClick: props.handleClickOnDeny,
                        disabled: props.disabledButtons.deny,
                    },
                    {
                        title: 'Approve',
                        onClick: props.handleClickOnApprove,
                        disabled: props.disabledButtons.approve,
                    },
                    {
                        title: 'Change log',
                        onClick: props.handleClickOnChangeLog,
                        dataCy: 'change-log-button',
                    },
                ];
                break;
            }
        case PurchaseRequest.tab.submitted:
            if (props.isAnyPRSelected) {
                items.push({
                    title: 'Copy',
                    onClick: props.handleMassCopy,
                    disabled: props.massCopyLoading || isCandex || isPunchOut || isNestBuy,
                });
                break;
            } else {
                items = [
                    {
                        title: 'Copy',
                        onClick: props.handleClickOnCopy,
                        disabled: props.disabledButtons.copy || isCandex || isPunchOut || isNestBuy,
                    },
                    {
                        title: 'Change log',
                        onClick: props.handleClickOnChangeLog,
                        dataCy: 'change-log-button',
                    },
                ];
                break;
            }
        case PurchaseRequest.tab.open:
            if (props.isAnyPRSelected) {
                items.push({
                    title: 'Submit',
                    onClick: props.handleMassSubmit,
                    disabled: props.massSubmitLoading,
                });
                items.push({
                    title: 'Copy',
                    onClick: props.handleMassCopy,
                    disabled: props.massCopyLoading || isCandex || isPunchOut || isNestBuy,
                });
                items.push({
                    title: 'Delete',
                    onClick: props.handleMassDelete,
                    disabled: props.massDeleteLoading,
                });
                break;
            } else {
                if (showButtons.rejectBtn) {
                    items.push({
                        title: 'Reject',
                        onClick: props.handleClickOnReject,
                        disabled: props.disabledButtons.save || props.isAnyPRSelected,
                    });
                }
                if (showButtons.saveBtn) {
                    items.push({
                        title: 'Save',
                        onClick: props.onSave,
                        disabled: props.disabledButtons.save || props.isAnyPRSelected,
                    });
                }
                if (showButtons.submitToRequesterBtn) {
                    items.push({
                        title: 'Submit to Requester',
                        onClick: props.handleClickOnSubmitToRequester,
                        disabled: props.disabledButtons.save || props.isAnyPRSelected,
                    });
                }
                if (showButtons.withdrawnBtn) {
                    items.push({
                        title: 'Withdrawn',
                        onClick: props.handleClickOnWithdrawn,
                        disabled: props.disabledButtons.save || props.isAnyPRSelected,
                    });
                }
                if (showButtons.submitBtn) {
                    items.push({
                        title: 'Submit',
                        onClick: props.isAnyPRSelected ? props.handleMassSubmit : props.onSubmit,
                        disabled: props.disabledButtons.submit,
                    });
                }
                items.push({
                    title: 'Copy',
                    onClick: props.handleClickOnCopy,
                    disabled: props.disabledButtons.copy || props.isAnyPRSelected || isCandex || isPunchOut || isNestBuy,
                });
                items.push({
                    title: 'Change log',
                    onClick: props.handleClickOnChangeLog,
                    disabled: props.isAnyPRSelected,
                    dataCy: 'change-log-button',
                });
                items.push({
                    title: 'Delete',
                    onClick: props.isAnyPRSelected ? props.handleMassDelete : handleClickOnDeletePR,
                    disabled: props.isAnyPRSelected,
                    dataCy: 'delete-button',
                });
                break;
            }
        }

        return (
            <ThreeDotsMenuShrunkenPR
                items={items}
                loading={props.loading}
                massSubmitLoading={props.massSubmitLoading}
                massCopyLoading={props.massCopyLoading}
                massDeleteLoading={props.massDeleteLoading}
                prTag={props.prTag}
                context={props.context}
                isAnyPRSelected={props.isAnyPRSelected}
                style={{color: '#4b4b4b'}}
            />
        );
    };

    const readableDate = creationDate.toLocaleString('en-US', READABLE_DATE_OPTIONS);

    const l1Category = Array.isArray(materials) && materials.length > 0 && materials[0].l1Category;

    const copyToClipboard = (event, value, callback) => {
        event.stopPropagation();
        setCopyToClipboardLeaveTime(2000);
        // Copy PR Tag to clipboard
        navigator.clipboard.writeText(value)
            .then(() => callback)
            .catch((e) => {
                console.log(e);
                return setCopyToClipboardText('Error');
            });
    };


    const addModifiedIcon = () => {
        if (status !== PurchaseRequest.statuses.created && lastActiveUser && !(lastActiveUser === spender.label || lastActiveUser === requester.label )) {
            return (
                <Tooltip title={lastActiveUser} placement={'top'} arrow>
                    <PriorityHighIcon sx={PRIORITY_HIGH_ICON_SX} />
                </Tooltip>
            );
        }
        return null;
    };

    const handleToggleSelectPR = useCallback((e) => {
        e.stopPropagation();

        const isFailedPR = props.failedMassSubmittedPRs.some((pr) => pr.prTag === props.prTag);

        if (isFailedPR) {
            props.setFailedMassSubmittedPRs((prev) => prev.filter((pr) => pr.prTag !== props.prTag));
        }

        if (props.selectedPRs.includes(props.prTag)) {
            props.setSelectedPRs((prev) => {
                const updatedPRs = prev.filter((pr) => pr !== props.prTag);
                if (updatedPRs.length === 0) {
                    props.setSelectPRsMode(false);
                }
                return updatedPRs;
            });
        } else {
            props.setSelectPRsMode(true);
            props.setSelectedPRs((prev) => [...prev, props.prTag]);
        }
    }, [status, props.failedMassSubmittedPRs, props.selectedPRs, props.prTag, props.setFailedMassSubmittedPRs, props.setSelectedPRs, props.setSelectPRsMode]);

    const handleDivClickAction = useCallback((e) => {
        if (props.selectPRsMode) {
            handleToggleSelectPR(e);
        } else {
            handleExpandPR();
        }
    }, [props.selectPRsMode, handleToggleSelectPR, handleExpandPR]);

    const columnsProps = {
        addTextWithTooltip,
        addTextWithTooltipMultiLines,
        addAvatar,
        addStatusIcon,
        addSupplierButton,
        copyToClipboard,
        setCopyToClipboardText,
        setCopyToClipboardLeaveTime,
        tagToDisplayER,
        removeLeadingZeros,
        copyToClipboardText,
        copyToClipboardTextPrNumber,
        copyToClipboardLeaveTime,
        readableDate,
        readableDateOptions: READABLE_DATE_OPTIONS,
        EPRId,
        prTag: props.prTag,
        prNumber,
        companyCode,
        source,
        sourceFromApiKey,
        spender,
        requester,
        projectTitle,
        vendorCode,
        vendorName,
        l1Category,
        materials,
        currentTab,
        getStringValueForSourceFromApiKey,
        setCopyToClipboardTextPrNumber,
        submissionDate,
        systemAlias,
        sourceToDisplay,
    };

    const isError = !!(errorMessages && errorMessages.length);
    const defaultBg = props.rowIndex % 2 === 0 ? '#ffffff' : '#f9f9f9';

    try {
        return (
            <>
                <Tooltip
                    title={errorMessages && errorMessages.length > 0 ? (
                        <div>
                            <strong>{errorTitle}</strong>
                            <ul>
                                {errorMessages.map((message, index) => (
                                    <li key={index}>{message}</li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        ''
                    )}
                    placement="top"
                    arrow
                >
                    <div
                        className={
                            isError ?
                                'pr-editor-shrunken-background-error' :
                                isSelected ?
                                    'pr-editor-shrunken-background-selected' :
                                    'pr-editor-shrunken-background'
                        }
                        onClick={handleDivClickAction}
                        style={
                            !isError && !isSelected ?
                                {backgroundColor: defaultBg} :
                                undefined
                        }
                    >
                        <Grid
                            container
                            columns={24}
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: 1,
                                height: 1,
                                m: 'auto',
                                fontSize: '16px',
                                textAlign: 'center',
                                color: '#707070',
                            }}
                        >
                            <Grid item xs={0.4}>
                                <Box display={'flex'} alignItems={'center'}>
                                    <TwoToneCheckbox
                                        checked={isSelected}
                                        onChange={handleToggleSelectPR}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    {addModifiedIcon()}
                                </Box>
                            </Grid>
                            {columnsToDisplay.map((columnName) => {
                                const columnConfig = COLUMN_CONTENT_CONFIG.find((config) => config.name === columnName);
                                if (columnConfig) {
                                    if (columnConfig.tab && !columnConfig.tab(currentTab)) {
                                        return null;
                                    }
                                    return (
                                        <Grid item xs={columnConfig.xs} sx={columnConfig.sx} key={columnName}>
                                            {columnConfig.content(columnsProps)}
                                        </Grid>
                                    );
                                }
                                return null;
                            })}
                            <Grid item xs={1} sx={{
                                color: ezprOrange,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                            }}>
                                {expandLoading ? (<CircularProgress sx={{color: '#4b4b4b !important'}} size={16}/>) : (<i style={{color: '#4b4b4b'}} className={'material-icons'}
                                    data-cy={`open_pr_${EPRId || tagToDisplayER(props.prTag)}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleExpandPR();
                                    }}>{'chevron_right'}</i>)}
                                {addThreeDotsMenu()}
                            </Grid>
                        </Grid>
                    </div>
                </Tooltip>
                <DefineSupplierPopup
                    onClose={handleCloseDefineSupplierPopup}
                    onCancel={handleCancelDefineSupplierPopup}
                    open={open}
                    selectedValue={undefined}
                    context={props.context}
                    prTag={props.prTag}
                    onClosePunchOut={handleClosePunchOut}
                />
            </>
        )
        ;
    } catch (e) {
        console.error(e);
        return null;
    }
}

ShrunkenPREditor.propTypes = {
    rowIndex: PropTypes.number,
    handleClickOnExpand: PropTypes.func,
    onSave: PropTypes.func,
    onSubmit: PropTypes.func,
    handleClickOnDeny: PropTypes.func,
    handleClickOnApprove: PropTypes.func,
    handleClickOnCopy: PropTypes.func,
    handleClickOnChangeLog: PropTypes.func,
    handleClickOnWithdrawn: PropTypes.func,
    handleClickOnSubmitToRequester: PropTypes.func,
    handleClickOnReject: PropTypes.func,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    prTag: PropTypes.string,
    loading: PropTypes.bool,
    disabledButtons: PropTypes.object,
    selectPRsMode: PropTypes.bool,
    setSelectPRsMode: PropTypes.func,
    selectedPRs: PropTypes.array,
    setSelectedPRs: PropTypes.func,
    failedMassSubmittedPRs: PropTypes.array,
    setFailedMassSubmittedPRs: PropTypes.func,
    isAnyPRSelected: PropTypes.bool,
    handleMassSubmit: PropTypes.func,
    massSubmitLoading: PropTypes.bool,
    massDeleteLoading: PropTypes.bool,
    handleMassDelete: PropTypes.func,
    massCopyLoading: PropTypes.bool,
    handleMassCopy: PropTypes.func,
    handleClickOnSupplier: PropTypes.func,
};
